import React, { useEffect, useMemo, useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaTwitter, FaFacebookF, FaInstagram, FaYoutube, FaLinkedinIn, FaPinterestP } from "react-icons/fa";
import { backend_url } from "../App";
import { SOCIAL_LINKS_DEFAULTS as D, SOCIAL_NETWORKS, SOCIAL_URL_MAX } from "../Constants/socialLinksDefaults";
import "./CSS/SocialLinksEditor.css";
import SaveBar from "../Components/SaveBar/SaveBar";

const KEY = "social-links";
const ICONS = {
  twitter: FaTwitter, facebook: FaFacebookF, instagram: FaInstagram,
  youtube: FaYoutube, linkedin: FaLinkedinIn, pinterest: FaPinterestP,
};
const IDS = SOCIAL_NETWORKS.map((n) => n.id);

// Saved data ↔ editor rows ({ id, url, visible }), in display order:
// visible networks first (in their saved order), then the hidden ones.
const toRows = (data) => {
  const v = { ...D, ...(data || {}) };
  const show = Array.isArray(v.show) && v.show.length ? v.show.filter((id) => IDS.includes(id)) : D.show;
  const order = [...show, ...IDS.filter((id) => !show.includes(id))];
  return order.map((id) => ({ id, url: v[id] || "", visible: show.includes(id) }));
};
const toData = (rows) => {
  const data = { show: rows.filter((r) => r.visible).map((r) => r.id) };
  rows.forEach((r) => { data[r.id] = r.url.trim(); });
  return data;
};

// Link checks shown under each row
const checkUrl = (net, url) => {
  const s = url.trim();
  if (!s) return { level: "empty" };
  let u;
  try { u = new URL(s); } catch { return { level: "error", text: "Not a full web address — start with https://" }; }
  if (u.protocol !== "https:" && u.protocol !== "http:") return { level: "error", text: "Must start with https://" };
  const host = u.hostname.replace(/^www\./, "").toLowerCase();
  if (!net.domains.some((d) => host === d || host.endsWith(`.${d}`)))
    return { level: "warn", text: `This doesn't look like a ${net.name} link (${host}).` };
  if (u.pathname === "/" || u.pathname === "")
    return { level: "warn", text: `Goes to the ${net.name} home page, not your page. Paste your page's link.` };
  return { level: "ok" };
};

const authHeaders = () => ({ "Content-Type": "application/json", Authorization: `Bearer ${sessionStorage.getItem("adminToken")}` });

const SocialLinksEditor = () => {
  const [rows, setRows]       = useState(toRows(null));
  const [saved, setSaved]     = useState(toRows(null));
  const [isCustom, setIsCustom] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [confirm, setConfirm] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${backend_url}/api/site-content/${KEY}`);
      const data = await res.json().catch(() => ({}));
      const r = toRows(data.success ? data.data : null);
      setRows(r); setSaved(r);
      setIsCustom(!!(data.success && data.data));
      if (res.status === 404) toast.info("Showing the original links. Saving needs the latest backend to be deployed.");
    } catch {
      const r = toRows(null); setRows(r); setSaved(r);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const dirty = JSON.stringify(toData(rows)) !== JSON.stringify(toData(saved)) ||
    JSON.stringify(rows.map((r) => r.id)) !== JSON.stringify(saved.map((r) => r.id));
  useEffect(() => {
    const onLeave = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", onLeave);
    return () => window.removeEventListener("beforeunload", onLeave);
  }, [dirty]);

  const update = (id, patch) => setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const move = (i, dir) => setRows((rs) => {
    const j = i + dir;
    if (j < 0 || j >= rs.length) return rs;
    const copy = [...rs]; [copy[i], copy[j]] = [copy[j], copy[i]]; return copy;
  });

  const checks = useMemo(
    () => Object.fromEntries(rows.map((r) => [r.id, checkUrl(SOCIAL_NETWORKS.find((n) => n.id === r.id), r.url)])),
    [rows]
  );
  // Problems that stop saving
  const blocking = rows.filter((r) => r.visible && (checks[r.id].level === "error" || checks[r.id].level === "empty"));
  const visibleCount = rows.filter((r) => r.visible).length;

  // Problems found before saving (shown in the save pop-up)
  const checkBeforeSave = () => {
    if (!visibleCount) return "Show at least one network — otherwise the original buttons come back.";
    if (!blocking.length) return null;
    const net = SOCIAL_NETWORKS.find((n) => n.id === blocking[0].id);
    return checks[blocking[0].id].level === "empty" ? `${net.name} is switched on but has no link.` : `${net.name}: ${checks[blocking[0].id].text}`;
  };

  // Returns { ok } or { ok: false, message } — the save pop-up shows the result
  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${backend_url}/api/site-content/${KEY}`, { method: "PUT", headers: authHeaders(), body: JSON.stringify({ data: toData(rows) }) });
      if (res.status === 404) return { ok: false, message: "The server doesn't have this feature yet — deploy the latest backend, then save again." };
      const data = await res.json().catch(() => ({}));
      if (res.status === 401 || data.message === "Invalid Token") return { ok: false, message: "Your login has expired — please log in again, then save." };
      if (!data.success) return { ok: false, message: data.message || "Could not save" };
      setSaved(rows);
      setIsCustom(true);
      return { ok: true };
    } catch {
      return { ok: false, message: "Could not reach the server — check your connection and try again." };
    } finally {
      setSaving(false);
    }
  };

  const resetAll = () => setConfirm({
    title: "Go back to the original links?", text: "Your saved social links are removed and the original four buttons come back.", yes: "Reset links",
    onYes: async () => {
      try {
        const res = await fetch(`${backend_url}/api/site-content/${KEY}`, { method: "DELETE", headers: authHeaders() });
        const data = await res.json().catch(() => ({}));
        if (data.success) { toast.success("Social links reset to the original"); load(); }
        else toast.error(data.message || "Could not reset");
      } catch { toast.error("Could not reset"); }
    },
  });

  if (loading) return <div className="sl-page"><p className="sl-muted">Loading…</p></div>;

  return (
    <div className="sl-page">
      <div className="sl-header">
        <div>
          <h1>Social Links</h1>
          <p>Choose which social media buttons appear in the website footer and where they go. The buttons keep their round design.</p>
        </div>
        <div className="sl-header-right">
          <span className={`sl-status ${isCustom ? "custom" : ""}`}>{isCustom ? "● Custom links" : "● Original links"}</span>
          {isCustom && <button className="sl-btn ghost" onClick={resetAll}>Reset to original</button>}
        </div>
      </div>

      {/* Preview — same look as the footer */}
      <section className="sl-preview" aria-label="Footer preview">
        <span className="sl-eyebrow">How it looks in the footer</span>
        <div className="sl-preview-row">
          {rows.filter((r) => r.visible && r.url.trim()).map((r) => {
            const Icon = ICONS[r.id];
            return (
              <a key={r.id} className="sl-circle" href={r.url} target="_blank" rel="noopener noreferrer"
                title={`${SOCIAL_NETWORKS.find((n) => n.id === r.id).name} — opens ${r.url}`}>
                <Icon size={14} />
              </a>
            );
          })}
          {!rows.some((r) => r.visible && r.url.trim()) && <span className="sl-preview-empty">No buttons switched on</span>}
        </div>
      </section>

      <ul className="sl-list" aria-label="Social networks">
        {rows.map((r, i) => {
          const net = SOCIAL_NETWORKS.find((n) => n.id === r.id);
          const Icon = ICONS[r.id];
          const check = checks[r.id];
          return (
            <li key={r.id} className={`sl-row ${r.visible ? "" : "off"}`}>
              <span className="sl-order">
                <button aria-label={`Move ${net.name} left`} title="Move left in the footer" disabled={i === 0} onClick={() => move(i, -1)}>▲</button>
                <button aria-label={`Move ${net.name} right`} title="Move right in the footer" disabled={i === rows.length - 1} onClick={() => move(i, 1)}>▼</button>
              </span>
              <span className={`sl-circle big ${r.visible ? "" : "muted"}`}><Icon size={16} /></span>
              <div className="sl-main">
                <div className="sl-top">
                  <label htmlFor={`sl-${r.id}`}>{net.name}</label>
                  <span className={`sl-counter ${r.url.length >= SOCIAL_URL_MAX ? "full" : ""}`}>{r.url.length}/{SOCIAL_URL_MAX}</span>
                </div>
                <div className="sl-url">
                  <input id={`sl-${r.id}`} value={r.url} maxLength={SOCIAL_URL_MAX} placeholder={net.example}
                    onChange={(e) => update(r.id, { url: e.target.value })} />
                  <a className={`sl-test ${check.level === "error" || !r.url.trim() ? "disabled" : ""}`}
                    href={check.level === "error" || !r.url.trim() ? undefined : r.url} target="_blank" rel="noopener noreferrer">
                    Open link ↗
                  </a>
                </div>
                {check.level === "error" && <p className="sl-msg error">⚠ {check.text}</p>}
                {check.level === "warn" && <p className="sl-msg warn">⚠ {check.text}</p>}
                {check.level === "empty" && r.visible && <p className="sl-msg error">⚠ Switched on but no link — add one or switch it off.</p>}
                {check.level === "ok" && <p className="sl-msg ok">✓ Looks good</p>}
              </div>
              <label className="sl-show">
                <button type="button" role="switch" aria-checked={r.visible} aria-label={`Show ${net.name} in the footer`}
                  className={`sl-switch ${r.visible ? "on" : ""}`} onClick={() => update(r.id, { visible: !r.visible })}>
                  <span />
                </button>
                <span>{r.visible ? "Shown" : "Hidden"}</span>
              </label>
            </li>
          );
        })}
      </ul>
      <p className="sl-tip">Tip: open your page on the social site, copy the address from the browser bar, and paste it here.</p>

      <SaveBar dirty={dirty} saving={saving} onSave={save} check={checkBeforeSave} what="the footer social links"
        onDiscard={() => setConfirm({ title: "Discard your changes?", text: "The links go back to how they were last saved.", yes: "Discard", onYes: () => setRows(saved) })} />

      {confirm && (
        <div className="sl-modal-overlay" onClick={() => setConfirm(null)}>
          <div className="sl-modal" role="dialog" aria-label={confirm.title} onClick={(e) => e.stopPropagation()}>
            <h3>{confirm.title}</h3>
            <p>{confirm.text}</p>
            <div className="sl-modal-actions">
              <button className="sl-btn ghost" onClick={() => setConfirm(null)}>Cancel</button>
              <button className="sl-btn danger" onClick={() => { confirm.onYes(); setConfirm(null); }}>{confirm.yes}</button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer />
    </div>
  );
};

export { toRows, toData, checkUrl };
export default SocialLinksEditor;
